/** Kit de UI da casca desktop: cabeçalho de página, card, KPI, campo, modal e
 * tabela densa genérica. Reusa controles do design-system (.m-input etc.). */
import { type ReactNode, useEffect, useId, useRef } from "react";

export function PageHeader({
  titulo,
  sub,
  acoes,
}: {
  titulo: string;
  sub?: string;
  acoes?: ReactNode;
}) {
  return (
    <div className="dk-page-head">
      <div>
        <div className="dk-page-title">{titulo}</div>
        {sub && <div className="dk-page-sub">{sub}</div>}
      </div>
      {acoes && <div className="dk-actions">{acoes}</div>}
    </div>
  );
}

export function Card({
  titulo,
  children,
  style,
}: {
  titulo?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="dk-card" style={style}>
      {titulo && <div className="dk-card-t">{titulo}</div>}
      {children}
    </div>
  );
}

export function Kpi({ rotulo, valor, cor }: { rotulo: string; valor: ReactNode; cor?: string }) {
  return (
    <div>
      <div className="dk-kpi-l">{rotulo}</div>
      <div className="dk-kpi-v" style={cor ? { color: cor } : undefined}>
        {valor}
      </div>
    </div>
  );
}

export function Field({ id, rotulo, children }: { id: string; rotulo: string; children: ReactNode }) {
  return (
    <div className="m-field">
      <label htmlFor={id}>{rotulo}</label>
      {children}
    </div>
  );
}

const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modais abertos, do mais antigo ao mais novo: só o do TOPO reage ao Esc. */
const pilhaModais: symbol[] = [];

export function Modal({
  titulo,
  sub,
  onClose,
  children,
  foot,
  largura,
  fecharNoFundo = true,
}: {
  titulo: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
  /** Largura máxima em px (padrão do CSS: 520). */
  largura?: number;
  /** `false` quando há digitação a perder: só o ✕ e o Esc fecham. */
  fecharNoFundo?: boolean;
}) {
  const idTitulo = useId();
  const caixa = useRef<HTMLDivElement>(null);
  const fechar = useRef(onClose);
  fechar.current = onClose;

  useEffect(() => {
    const token = Symbol("modal");
    pilhaModais.push(token);
    const quemAbriu = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Foco entra no diálogo: 1º campo do corpo, senão o próprio diálogo. Sem
    // isto o teclado continuava na página de trás (e o Esc nem chegava aqui).
    const el = caixa.current;
    if (el && !el.contains(document.activeElement)) {
      const alvo = el.querySelector<HTMLElement>(
        ".dk-modal-body input, .dk-modal-body select, .dk-modal-body textarea",
      );
      (alvo ?? el).focus({ preventScroll: true });
    }
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && pilhaModais[pilhaModais.length - 1] === token) {
        fechar.current();
      }
    };
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      const i = pilhaModais.indexOf(token);
      if (i !== -1) {
        pilhaModais.splice(i, 1);
      }
      // Devolve o foco a quem abriu (se ainda estiver na página).
      if (quemAbriu?.isConnected) {
        quemAbriu.focus({ preventScroll: true });
      }
    };
  }, []);

  /** Tab não escapa para a página de trás. */
  const prenderFoco = (e: React.KeyboardEvent) => {
    const el = caixa.current;
    if (e.key !== "Tab" || !el) {
      return;
    }
    // Fora os que o Tab não alcança (ex.: o submit oculto de um formulário).
    const itens = [...el.querySelectorAll<HTMLElement>(FOCAVEIS)].filter((x) => !x.hidden && x.tabIndex >= 0);
    const primeiro = itens[0];
    const ultimo = itens[itens.length - 1];
    if (!primeiro || !ultimo) {
      e.preventDefault();
      return;
    }
    const ativo = document.activeElement;
    if (e.shiftKey && (ativo === primeiro || ativo === el)) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && ativo === ultimo) {
      e.preventDefault();
      primeiro.focus();
    }
  };

  return (
    <div
      className="dk-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        // mousedown (não click): arrastar a seleção de um campo para fora do
        // diálogo terminava com o "click" no fundo e fechava o modal. Só o botão
        // principal fecha — como o click fazia; o direito abre o menu de contexto.
        if (fecharNoFundo && e.button === 0 && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={caixa}
        className="dk-modal"
        // biome-ignore lint/a11y/useSemanticElements: diálogo customizado (scrim/posição próprios)
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        style={largura ? { width: `min(${largura}px, 100%)` } : undefined}
        onKeyDown={prenderFoco}
      >
        <div className="dk-modal-head">
          <div style={{ minWidth: 0 }}>
            <div className="dk-page-title" id={idTitulo} style={{ fontSize: 18 }}>
              {titulo}
            </div>
            {sub && <div className="dk-page-sub">{sub}</div>}
          </div>
          <button type="button" className="dk-x" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className="dk-modal-body">{children}</div>
        {foot && <div className="dk-modal-foot">{foot}</div>}
      </div>
    </div>
  );
}

export interface Coluna<T> {
  rotulo: string;
  celula: (linha: T) => ReactNode;
  largura?: string;
  num?: boolean;
}

/** Tabela densa genérica. `aoClicar` torna as linhas interativas (acessível). */
export function Tabela<T>({
  colunas,
  linhas,
  chave,
  aoClicar,
  ativo,
  vazio,
}: {
  colunas: Coluna<T>[];
  linhas: readonly T[];
  chave: (l: T) => string;
  aoClicar?: (l: T) => void;
  ativo?: (l: T) => boolean;
  vazio?: string;
}) {
  return (
    <div className="dk-table-wrap">
      <table className="dk-table">
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c.rotulo} style={{ width: c.largura, textAlign: c.num ? "right" : undefined }}>
                {c.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <td colSpan={colunas.length}>
                <div className="dk-vazio">{vazio ?? "Nada a exibir."}</div>
              </td>
            </tr>
          )}
          {linhas.map((l) => (
            <tr
              key={chave(l)}
              className={`${aoClicar ? "clic" : ""}${ativo?.(l) ? " on" : ""}`}
              {...(aoClicar
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: () => aoClicar(l),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        aoClicar(l);
                      }
                    },
                  }
                : {})}
            >
              {colunas.map((c) => (
                <td
                  key={c.rotulo}
                  className={c.num ? "dk-num" : undefined}
                  style={{ textAlign: c.num ? "right" : undefined }}
                >
                  {c.celula(l)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
